import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { IbanElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { z } from "zod";

import {
  Box,
  Button,
  Loader,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { ModalInstance } from "@js/components/modal";
import { Snackbar } from "@js/components/snackbar";
import { RhfTextField } from "@js/rhf/fields";

import {
  useCreateStripeSEPAPaymentMethodMutation,
  useCreateStripeSEPASetupIntentMutation,
} from "../../api";
import NullStripeFeedback from "../null-stripe-feedback";

import styles from "./styles.module.scss";

const sepaSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
});

type SEPAForm = z.infer<typeof sepaSchema>;

export const AddSEPAPaymentMethodModalContent = () => {
  const stripe = useStripe();
  const elements = useElements();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<SEPAForm>({
    resolver: zodResolver(sepaSchema),
  });

  const [createStripeSEPASetupIntent] =
    useCreateStripeSEPASetupIntentMutation();
  const [createStripeSEPAPaymentMethod] =
    useCreateStripeSEPAPaymentMethodMutation();

  const onSubmit = async (data: SEPAForm) => {
    try {
      if (!stripe || !elements) {
        Snackbar.error(
          "Stripe is not available, please refresh the page and try again.",
        );
        return;
      }
      const ibanElement = elements.getElement(IbanElement);
      if (!ibanElement) {
        Snackbar.error("IBAN input is not available.");
        return;
      }

      const { client_secret } = await createStripeSEPASetupIntent().unwrap();

      const { error: setupIntentError, setupIntent } =
        await stripe.confirmSepaDebitSetup(client_secret, {
          payment_method: {
            sepa_debit: ibanElement,
            billing_details: {
              name: data.name,
              email: data.email,
            },
          },
        });

      const setupIntentId = setupIntent?.id;
      if (setupIntentError || !setupIntentId) {
        Snackbar.error(
          setupIntentError?.message || "Failed to create payment method",
        );
        return;
      }

      await createStripeSEPAPaymentMethod({
        setup_intent_id: setupIntentId,
      }).unwrap();

      Snackbar.success("SEPA payment method added successfully");
      ModalInstance.close();
    } catch (_error: unknown) {
      Snackbar.error("Failed to add payment method, please try again");
    }
  };

  return (
    <Stack component="form" onSubmit={handleSubmit(onSubmit)} sx={{ gap: 2 }}>
      <Typography
        component="h2"
        variant="paragraph"
        fontWeight={400}
        sx={{
          maxWidth: "85%",
          mb: 2,
          fontSize: { xs: "20px !important", sm: "26px !important" },
        }}
      >
        Add SEPA Payment Method
      </Typography>
      <NullStripeFeedback stripe={stripe}>
        <RhfTextField
          id="name"
          name="name"
          control={control}
          label="Account Holder Name"
        />
        <RhfTextField id="email" name="email" control={control} label="Email" />
        <IBANInput />
        <Stack
          direction="row"
          sx={{
            gap: 1,
            justifyContent: "end",
            mt: { xs: 2, sm: 4 },
            flexWrap: "wrap",
          }}
        >
          <Button
            type="button"
            variant="secondary"
            disabled={isSubmitting}
            onClick={ModalInstance.close}
            size="small"
          >
            Close
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            size="small"
            endIcon={isSubmitting ? <Loader size={20} /> : undefined}
          >
            Add SEPA Payment Method
          </Button>
        </Stack>
      </NullStripeFeedback>
    </Stack>
  );
};

const IBANInput = () => {
  const [isFocused, setIsFocused] = useState(false);
  return (
    // wrap with label to focus iban element on container click
    <Box component="label" htmlFor="iban-element" sx={{ display: "block" }}>
      <Typography
        variant="label"
        component="span"
        size="medium"
        sx={{ mb: 1, display: "block" }}
      >
        IBAN
      </Typography>
      <Box
        className="transition"
        sx={{
          p: "15px 12px;",
          transition: "var(--transition)",
          border: "1px solid var(--grey-5)",
          boxShadow: isFocused
            ? "0 0 0 2px var(--black);"
            : "0 0 0 0px var(--black);",
          borderRadius: 1,
        }}
      >
        <IbanElement
          id="iban-element"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          options={{
            supportedCountries: ["SEPA"],
            placeholderCountry: "DE",
            style: {
              base: { fontSize: "16px" },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export const openAddSEPAPaymentMethodModal = () => {
  ModalInstance.open({
    children: <AddSEPAPaymentMethodModalContent />,
    className: styles.modal,
    keepOnBackdropClick: true,
    closeButton: true,
  });
};
